import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { DirectSecp256k1HdWallet } from '@cosmjs/proto-signing';
import { SigningStargateClient, StargateClient } from '@cosmjs/stargate';
import { stringToPath } from '@cosmjs/crypto';

const CHAIN_ID = 'amitis-network';
const RPC = 'https://rpc.amitis.network';
const REST = 'https://rest.amitis.network';
const DENOM = 'uamts';
const BECH32_PREFIX = 'amitis';
const COIN_DECIMALS = 6;

const CHAIN_INFO = {
  chainId: CHAIN_ID,
  chainName: 'Amitis Network',
  rpc: RPC,
  rest: REST,
  bip44: { coinType: 118 },
  bech32Config: {
    bech32PrefixAccAddr: BECH32_PREFIX,
    bech32PrefixAccPub: `${BECH32_PREFIX}pub`,
    bech32PrefixValAddr: `${BECH32_PREFIX}valoper`,
    bech32PrefixValPub: `${BECH32_PREFIX}valoperpub`,
    bech32PrefixConsAddr: `${BECH32_PREFIX}valcons`,
    bech32PrefixConsPub: `${BECH32_PREFIX}valconspub`,
  },
  currencies: [{ coinDenom: 'AMTS', coinMinimalDenom: DENOM, coinDecimals: COIN_DECIMALS }],
  feeCurrencies: [{ coinDenom: 'AMTS', coinMinimalDenom: DENOM, coinDecimals: COIN_DECIMALS, gasPriceStep: { low: 0.01, average: 0.025, high: 0.04 } }],
  stakeCurrency: { coinDenom: 'AMTS', coinMinimalDenom: DENOM, coinDecimals: COIN_DECIMALS },
};

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
  const [wallet, setWallet] = useState(null); // { address, type: 'keplr'|'browser', mnemonic? }
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);

  const fetchBalance = useCallback(async (address) => {
    try {
      const res = await fetch(`${REST}/cosmos/bank/v1beta1/balances/${address}`);
      const data = await res.json();
      const amts = data.balances?.find(b => b.denom === DENOM);
      setBalance(amts ? (parseInt(amts.amount) / Math.pow(10, COIN_DECIMALS)) : 0);
    } catch (e) {
      setBalance(0);
    }
  }, []);

  useEffect(() => {
    if (wallet?.address) {
      fetchBalance(wallet.address);
      const interval = setInterval(() => fetchBalance(wallet.address), 10000);
      return () => clearInterval(interval);
    }
  }, [wallet, fetchBalance]);

  // Connect Keplr
  const connectKeplr = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!window.keplr) throw new Error('Keplr not installed. Please install the Keplr browser extension.');
      
      // Try to suggest the chain
      try {
        await window.keplr.experimentalSuggestChain(CHAIN_INFO);
      } catch (e) {
        // Chain may already be added
      }
      
      await window.keplr.enable(CHAIN_ID);
      const offlineSigner = window.keplr.getOfflineSigner(CHAIN_ID);
      const accounts = await offlineSigner.getAccounts();
      const address = accounts[0].address;
      
      const signingClient = await SigningStargateClient.connectWithSigner(RPC, offlineSigner);
      setClient(signingClient);
      setWallet({ address, type: 'keplr' });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Generate browser wallet — returns mnemonic for display
  const generateWallet = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hdWallet = await DirectSecp256k1HdWallet.generate(24, {
        prefix: BECH32_PREFIX,
        hdPaths: [stringToPath("m/44'/118'/0'/0/0")],
      });
      const accounts = await hdWallet.getAccounts();
      const address = accounts[0].address;
      const mnemonic = hdWallet.mnemonic;
      
      const signingClient = await SigningStargateClient.connectWithSigner(RPC, hdWallet);
      setClient(signingClient);
      setWallet({ address, type: 'browser', mnemonic });
      return mnemonic; // Return so modal can display it
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Import wallet from mnemonic
  const importWallet = useCallback(async (mnemonic) => {
    setLoading(true);
    setError(null);
    try {
      const hdWallet = await DirectSecp256k1HdWallet.fromMnemonic(mnemonic.trim(), {
        prefix: BECH32_PREFIX,
        hdPaths: [stringToPath("m/44'/118'/0'/0/0")],
      });
      const accounts = await hdWallet.getAccounts();
      const address = accounts[0].address;
      
      const signingClient = await SigningStargateClient.connectWithSigner(RPC, hdWallet);
      setClient(signingClient);
      setWallet({ address, type: 'browser', mnemonic });
    } catch (e) {
      setError(e.message || 'Invalid mnemonic');
    } finally {
      setLoading(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    setWallet(null);
    setBalance(null);
    setClient(null);
    setError(null);
  }, []);

  const sendTokens = useCallback(async (toAddress, amountAmts) => {
    if (!client || !wallet) throw new Error('Wallet not connected');
    const amount = [{ denom: DENOM, amount: String(Math.floor(amountAmts * Math.pow(10, COIN_DECIMALS))) }];
    const fee = { amount: [{ denom: DENOM, amount: '150000' }], gas: '200000' };
    const result = await client.sendTokens(wallet.address, toAddress, amount, fee);
    await fetchBalance(wallet.address);
    return result;
  }, [client, wallet, fetchBalance]);

  return (
    <WalletContext.Provider value={{
      wallet, balance, loading, error,
      connectKeplr, generateWallet, importWallet, disconnect, sendTokens,
      fetchBalance,
      CHAIN_ID, RPC, REST, DENOM, COIN_DECIMALS, BECH32_PREFIX,
    }}>
      {children}
    </WalletContext.Provider>
  );
}

export const useWallet = () => useContext(WalletContext);
